// Admin MFA enrollment (Phase 5c). Mandatory per §3a — admins must have
// MFA enrolled before the cookie can carry mfa-ok=true. The first POST
// returns an otpauth:// URI; the user enters the code from their authenticator
// app and we Complete which persists the encrypted secret + returns recovery
// codes for one-time display.
//
// QR rendering: the otpauth:// URI is rendered as a scannable QR code (qrcode.react),
// with the Base32 secret kept behind a "can't scan?" fallback for manual entry.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import type {
  AdminMfaEnrollBeginResponse,
  AdminMfaEnrollCompleteRequest,
  AdminMfaEnrollCompleteResponse,
} from '@shared/api-types';
import { ApiError, adminApiFetch } from '../../lib/adminApiClient';
import { useAdminAuth } from '../../lib/adminAuth';

export function MfaEnroll() {
  const navigate = useNavigate();
  const { refresh } = useAdminAuth();

  const [begin, setBegin] = useState<AdminMfaEnrollBeginResponse | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const startEnrollment = useCallback(async () => {
    setError(null);
    try {
      const resp = await adminApiFetch<AdminMfaEnrollBeginResponse>(
        '/api/admin/auth/mfa/enroll/begin',
        { method: 'POST' },
      );
      setBegin(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start enrollment.');
    }
  }, []);

  // Kick off enrollment on mount. Inlined behind an await boundary (rather than
  // calling startEnrollment, which sets state synchronously) to avoid the
  // setState-in-effect cascading-render warning. The Retry button still uses
  // startEnrollment.
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const resp = await adminApiFetch<AdminMfaEnrollBeginResponse>(
          '/api/admin/auth/mfa/enroll/begin',
          { method: 'POST' },
        );
        if (active) setBegin(resp);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Could not start enrollment.');
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!begin) return;
    setLoading(true);
    setError(null);
    try {
      const body: AdminMfaEnrollCompleteRequest = {
        secretBase32: begin.secretBase32,
        code,
      };
      const resp = await adminApiFetch<AdminMfaEnrollCompleteResponse>(
        '/api/admin/auth/mfa/enroll/complete',
        { method: 'POST', body },
      );
      setRecoveryCodes(resp.recoveryCodes);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError('That code did not verify. Please try again.');
      } else {
        setError('Enrollment failed. Please retry.');
      }
    } finally {
      setLoading(false);
    }
  };

  const finishAndGoHome = async () => {
    await refresh();
    navigate('/', { replace: true });
  };

  if (!begin && !error) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Loader size="large" type="converging-spinner" />
      </div>
    );
  }

  if (recoveryCodes) {
    return (
      <div style={panelStyle}>
        <h2>Save your recovery codes</h2>
        <p style={{ color: '#475569', marginTop: 0 }}>
          Store these somewhere safe — they are shown <strong>only once</strong>.
        </p>
        <ul style={{ fontFamily: 'ui-monospace, Consolas, monospace', columns: 2, padding: 0, listStyle: 'none' }}>
          {recoveryCodes.map((c) => (
            <li key={c} style={{ padding: '4px 0' }}>{c}</li>
          ))}
        </ul>
        <Button themeColor="primary" onClick={finishAndGoHome} data-testid="mfa-enroll-done">
          I've saved them — continue
        </Button>
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <h2>Set up two-factor authentication</h2>
      <p style={{ color: '#475569' }}>
        Scan this QR code with your authenticator app (Microsoft Authenticator, 1Password, Authy, etc.),
        then enter the 6-digit code it shows.
      </p>
      {begin && (
        <>
          <div style={{ display: 'grid', placeItems: 'center', margin: '8px 0 16px' }}>
            <div style={{ background: '#fff', padding: 16, borderRadius: 12, boxShadow: '0 1px 3px rgba(15,23,42,0.12)' }}>
              <QRCodeSVG value={begin.otpAuthUri} size={192} marginSize={1} aria-label="MFA enrollment QR code" />
            </div>
          </div>
          <details>
            <summary style={{ fontSize: 12, color: '#475569', fontWeight: 600, cursor: 'pointer' }}>
              Can't scan it? Enter this key manually
            </summary>
            <pre style={uriStyle}>{begin.secretBase32}</pre>
            <Button
              size="small"
              onClick={() => void navigator.clipboard?.writeText(begin.secretBase32)}
            >
              Copy key
            </Button>
          </details>
          <form onSubmit={handleComplete} style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>6-digit code</label>
            <input
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{ padding: 10, fontSize: 18, letterSpacing: '0.3em', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: 8 }}
              data-testid="mfa-code-input"
            />
            {error && <div style={{ color: '#dc2626', fontSize: 13 }} role="alert">{error}</div>}
            <Button
              themeColor="primary"
              type="submit"
              disabled={loading || code.length !== 6}
              data-testid="mfa-enroll-submit"
            >
              {loading ? 'Verifying…' : 'Verify and enable MFA'}
            </Button>
          </form>
        </>
      )}
      {!begin && error && (
        <>
          <p style={{ color: '#dc2626' }}>{error}</p>
          <Button onClick={() => void startEnrollment()}>Retry</Button>
        </>
      )}
    </div>
  );
}

const panelStyle: React.CSSProperties = {
  maxWidth: 520,
  margin: '40px auto',
  padding: 32,
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
};

const uriStyle: React.CSSProperties = {
  background: '#f1f5f9',
  padding: 12,
  borderRadius: 8,
  fontSize: 12,
  overflowX: 'auto',
  margin: '4px 0 12px',
};
